import { Link, Shimmer } from '@legacy/components/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetNotificationCenter,
  useGetIsApprovalEnabled,
} from '@legacy/apis/notificationCenterAPI';
import { ResponseHandler } from '@legacy/components/utils/ResponseHandler';
import { WidgetHeader } from '@legacy/screens/Home/components/WidgetHeader';
import { formatUrl } from '@legacy/utils/url';
import { useLocalStorage } from '@legacy/hooks/useLocalStorage';
import {
  Alert,
  Button,
  Stack,
  Typography,
} from '@legacy/components/mui-components';
import { GppMaybeOutlined } from '@mui/icons-material';
import { AccordionItem, InfoModal, ListItem, NoResults } from './components';

export const notificationCenterExpandAllStateLocalStorageKey =
  'notification-center-expand-state';

export const NotificationCenter = () => {
  const {
    timeSheetsToApprove,
    absenceToApprove,
    projectTimeToApprove,
    itemsToTakeActionOn,
    notificationCenterTimesheetItems,
    numberOfTravelExpensesPendingApproval = 0,
    numberOfExpensesPendingApproval = 0,
    numberOfMileagePendingApproval = 0,
    approveExpensesUrl,
    isApprovalOfExpensesEnabled,
    isApprovalOfTimeSheetEnabled,
    isManagerForActiveEmployees,
    isApprovalOfTimesheetByProjectManagerOnlyEnabled,
    isApprovalOfTimesheetByEmployeeManagerOnlyEnabled,
    isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled,
    closeTimeSheetReportUrl,
    approvalUrl,
    timeSheetsToBeClosedLength,
    timeSheetsToApproveLength,
    isError,
    isLoading,
    hasApprovalProjectRole,
  } = useGetNotificationCenter();
  const { isApprovalEnabled } = useGetIsApprovalEnabled();
  const { t } = useTranslation('notificationCenter');

  const totalExpensesForApproval =
    numberOfTravelExpensesPendingApproval + numberOfExpensesPendingApproval;
  const expenseReportQueryParams =
    totalExpensesForApproval == 0 ||
    (numberOfExpensesPendingApproval > 0 &&
      numberOfTravelExpensesPendingApproval > 0)
      ? ''
      : numberOfExpensesPendingApproval > 0
        ? '?expenses=true'
        : '?travelExpenses=true';

  // Extract meaningful condition variables
  const showTimesheetsToClose =
    isApprovalOfTimeSheetEnabled &&
    (isApprovalOfTimesheetByEmployeeManagerOnlyEnabled ||
      isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled) &&
    notificationCenterTimesheetItems &&
    isManagerForActiveEmployees;

  const showProjectTimeToApprove =
    projectTimeToApprove &&
    (isApprovalOfTimesheetByProjectManagerOnlyEnabled ||
      isApprovalOfTimesheetByProjectManagerThenEmployeeManagerEnabled) &&
    hasApprovalProjectRole;

  const showTimesheetsToApprove =
    timeSheetsToApprove &&
    !isApprovalOfTimesheetByProjectManagerOnlyEnabled &&
    isManagerForActiveEmployees;

  const showAbsenceToApprove = absenceToApprove && isManagerForActiveEmployees;

  const showExpensesToApprove = isApprovalOfExpensesEnabled;

  const showMileageToApprove = isApprovalOfExpensesEnabled;

  // Create dynamic mapping of accordion items to indices
  const accordionMapping = useMemo(() => {
    const mapping: Record<string, number> = {};
    let currentIndex = 0;

    // Define accordion items in render order
    const accordionItems = [
      { key: 'timesheetsToBeClosed', shouldRender: showTimesheetsToClose },
      { key: 'projectTimeToApprove', shouldRender: showProjectTimeToApprove },
      { key: 'timesheetsToApprove', shouldRender: showTimesheetsToApprove },
      { key: 'absenceToApprove', shouldRender: showAbsenceToApprove },
      { key: 'expensesToApprove', shouldRender: showExpensesToApprove },
      { key: 'mileageToApprove', shouldRender: showMileageToApprove },
    ];

    // Assign sequential indices only to rendered items
    accordionItems.forEach((item) => {
      if (item.shouldRender) {
        mapping[item.key] = currentIndex++;
      }
    });

    return mapping;
  }, [
    showTimesheetsToClose,
    showProjectTimeToApprove,
    showTimesheetsToApprove,
    showAbsenceToApprove,
    showExpensesToApprove,
    showMileageToApprove,
  ]);

  const totalAccordionItems = Object.keys(accordionMapping).length;
  const defaultIndices = useMemo(
    () => Array.from({ length: totalAccordionItems }, (_value, index) => index),
    [totalAccordionItems],
  );

  const [indices, setIndices] = useLocalStorage(
    notificationCenterExpandAllStateLocalStorageKey,
    defaultIndices,
  );

  const showNotificationsCenter =
    isApprovalEnabled &&
    (isApprovalOfTimeSheetEnabled || isApprovalOfExpensesEnabled);

  const showNoContentForNonManager =
    showNotificationsCenter &&
    itemsToTakeActionOn === 0 &&
    !isManagerForActiveEmployees;

  const expandCollapseHandler = () => {
    if (indices.length === defaultIndices.length) {
      return setIndices([]);
    }

    return setIndices(defaultIndices);
  };

  const toggleItem = (toggledIndex: number) => {
    if (indices.includes(toggledIndex)) {
      setIndices(
        indices.filter((currentIndex) => currentIndex !== toggledIndex),
      );
    } else {
      setIndices([...indices, toggledIndex].sort());
    }
  };

  return (
    <ResponseHandler
      isError={isError}
      isLoading={isLoading}
      LoadingComponent={<Shimmer />}
      ErrorComponent={
        <Alert color="error" data-automation-id="NotificationCenterAlertError">
          <Typography>
            {t('CouldNotLoadNotificationCenterErrorMessage')}
          </Typography>
        </Alert>
      }
    >
      {showNotificationsCenter ? (
        <Stack gap={2} data-automation-id="NotificationCenter">
          <WidgetHeader
            widgetKey="NotificationCenter"
            heading={t('Heading')}
            headingAs="h3"
            icon={
              <GppMaybeOutlined data-automation-id="NotificationCenterHeaderIcon" />
            }
            count={itemsToTakeActionOn}
            data-automation-id="NotificationCenterHeader"
            action={<InfoModal />}
          />
          {showNoContentForNonManager ? (
            <NoResults />
          ) : (
            <div>
              {totalAccordionItems > 0 && (
                <Stack direction="row" justifyContent="end">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={expandCollapseHandler}
                    aria-owns="notification-center-accordion"
                    data-automation-id="NotificationCenterExpandCollapseButton"
                  >
                    {indices.length !== defaultIndices.length
                      ? t('ExpandAllButtonText')
                      : t('CollapseAllButtonText')}
                  </Button>
                </Stack>
              )}

              <div id="notification-center-accordion">
                {isApprovalOfTimeSheetEnabled && (
                  <>
                    {/* Timesheets to close - shown for employee managers & two level approval*/}
                    {showTimesheetsToClose && (
                      <AccordionItem
                        buttonAutomationId="TimeSheetsToBeClosedHeading"
                        heading={t('TimeSheetsToCloseHeading')}
                        counter={timeSheetsToBeClosedLength}
                        dataAutomationId="NotificationCenterItemTimeSheetsToBeClosed"
                        handleChange={() =>
                          toggleItem(accordionMapping.timesheetsToBeClosed)
                        }
                        expanded={indices.includes(
                          accordionMapping.timesheetsToBeClosed,
                        )}
                      >
                        {notificationCenterTimesheetItems.length > 0 ? (
                          <>
                            <Stack>
                              {notificationCenterTimesheetItems.map(
                                (item, index) =>
                                  item.weeklyTimesheetDetails.map(
                                    (timeSheet, childIndex) => (
                                      <ListItem
                                        name={item.employeeFullName}
                                        employeeId={item.employeeUserId}
                                        hours={timeSheet.hours}
                                        normalWorkingTimeHoursInPeriod={
                                          timeSheet.normalWorkingTime
                                        }
                                        startDate={timeSheet.startDate}
                                        endDate={timeSheet.endDate}
                                        // we can't get and id from backend
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`timesheet-items-${item.employeeUserId}-${index}-${childIndex}`}
                                      />
                                    ),
                                  ),
                              )}
                            </Stack>

                            <Link
                              href={formatUrl(closeTimeSheetReportUrl)}
                              data-automation-id="CloseTimeSheetsReportLink"
                            >
                              {t('TimeSheetsToCloseLinkText')}
                            </Link>
                          </>
                        ) : (
                          <Alert
                            color="success"
                            data-automation-id="NotificationCenterAlertTimeSheetsToBeClosed"
                          >
                            <Typography>
                              {t('TimeSheetsToCloseDoneText')}
                            </Typography>
                          </Alert>
                        )}
                      </AccordionItem>
                    )}

                    {/* Approve time - shown for project managers or in combined approval mode */}
                    {showProjectTimeToApprove && (
                      <AccordionItem
                        buttonAutomationId="TimeSheetsToApproveHeading"
                        heading={t('TimeToApproveHeading')}
                        counter={projectTimeToApprove.length}
                        dataAutomationId="NotificationCenterItemTimeSheets"
                        handleChange={() =>
                          toggleItem(accordionMapping.projectTimeToApprove)
                        }
                        expanded={indices.includes(
                          accordionMapping.projectTimeToApprove,
                        )}
                      >
                        {projectTimeToApprove.length > 0 ? (
                          <>
                            <Stack>
                              {projectTimeToApprove.map((item, index) => (
                                <ListItem
                                  name={item.projectName}
                                  employeeId={item.projectId}
                                  hours={item.hours}
                                  normalWorkingTimeHoursInPeriod={
                                    item.normalWorkingTimeHoursInPeriod
                                  }
                                  startDate={item.startDate}
                                  endDate={item.endDate}
                                  url={item.approvalForUserUrl}
                                  linkText={t(
                                    'TimeSheetsToApproveSingleLinkToolTip',
                                    {
                                      EMPLOYEE_NAME: item.employeeInfo,
                                    },
                                  )}
                                  allocatedEmployees={item.allocatedEmployees}
                                  navigationItemType={item.navigationItemType}
                                  projectId={item.projectId}
                                  // we can't get and id from backend
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`timesheet-approve-${item.employeeUserId}-${index}`}
                                />
                              ))}
                            </Stack>

                            <Link
                              href={formatUrl(approvalUrl)}
                              data-automation-id="ApproveTimeSheetsLink"
                            >
                              {t('TimeSheetsToApproveLinkText')}
                            </Link>
                          </>
                        ) : (
                          <Alert
                            color="success"
                            data-automation-id="NotificationCenterAlertProjectTimeToBeApproved"
                          >
                            <Typography>
                              {t('TimeSheetsToApproveDoneText')}
                            </Typography>
                          </Alert>
                        )}
                      </AccordionItem>
                    )}

                    {/* Approve timesheets - shown when NOT in project manager only mode */}
                    {showTimesheetsToApprove && (
                      <AccordionItem
                        buttonAutomationId="TimeSheetsToApproveHeading"
                        heading={t('TimeSheetsToApproveHeading')}
                        counter={timeSheetsToApproveLength}
                        dataAutomationId="NotificationCenterItemTimeSheets"
                        handleChange={() =>
                          toggleItem(accordionMapping.timesheetsToApprove)
                        }
                        expanded={indices.includes(
                          accordionMapping.timesheetsToApprove,
                        )}
                      >
                        {timeSheetsToApprove.length > 0 ? (
                          <>
                            <Stack>
                              {timeSheetsToApprove.map((item, index) => (
                                <ListItem
                                  name={item.employeeInfo}
                                  employeeId={item.employeeUserId}
                                  hours={item.hours}
                                  normalWorkingTimeHoursInPeriod={
                                    item.normalWorkingTimeHoursInPeriod
                                  }
                                  startDate={item.startDate}
                                  endDate={item.endDate}
                                  url={item.approvalForUserUrl}
                                  linkText={t(
                                    'TimeSheetsToApproveSingleLinkToolTip',
                                    {
                                      EMPLOYEE_NAME: item.employeeInfo,
                                    },
                                  )}
                                  navigationItemType={item.navigationItemType}
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`timesheet-approve-${item.employeeUserId}-${index}`}
                                />
                              ))}
                            </Stack>

                            <Link
                              href={formatUrl(approvalUrl)}
                              data-automation-id="ApproveTimeSheetsLink"
                            >
                              {t('TimeSheetsToApproveLinkText')}
                            </Link>
                          </>
                        ) : (
                          <Alert
                            color="success"
                            data-automation-id="NotificationCenterAlertTimeSheetsToBeApproved"
                          >
                            <Typography>
                              {t('TimeSheetsToApproveDoneText')}
                            </Typography>
                          </Alert>
                        )}
                      </AccordionItem>
                    )}

                    {showAbsenceToApprove && (
                      <AccordionItem
                        buttonAutomationId="AbsenceToApproveHeading"
                        heading={t('AbsenceToApproveHeading')}
                        counter={absenceToApprove.length}
                        dataAutomationId="NotificationCenterItemAbsence"
                        handleChange={() =>
                          toggleItem(accordionMapping.absenceToApprove)
                        }
                        expanded={indices.includes(
                          accordionMapping.absenceToApprove,
                        )}
                      >
                        {absenceToApprove.length > 0 ? (
                          <>
                            <Stack>
                              {absenceToApprove.map((item, index) => (
                                <ListItem
                                  name={item.employeeInfo}
                                  employeeId={item.employeeUserId}
                                  url={item.approvalForUserUrl}
                                  linkText={t(
                                    'AbsenceToApproveSingleLinkToolTip',
                                    {
                                      EMPLOYEE_NAME: item.employeeInfo,
                                    },
                                  )}
                                  navigationItemType={item.navigationItemType}
                                  startDate={item.startDate}
                                  endDate={item.endDate}
                                  // we can't get and id from backend
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={`absence-${item.employeeUserId}-${index}`}
                                />
                              ))}
                            </Stack>

                            <Link
                              href={formatUrl(approvalUrl)}
                              data-automation-id="ApproveAbsenceLink"
                            >
                              {t('AbsenceToApproveLinkText')}
                            </Link>
                          </>
                        ) : (
                          <Alert
                            color="success"
                            data-automation-id="NotificationCenterAlertAbsenceToBeApproved"
                          >
                            <Typography>
                              {t('AbsenceToApproveDoneText')}
                            </Typography>
                          </Alert>
                        )}
                      </AccordionItem>
                    )}
                  </>
                )}

                {showExpensesToApprove && (
                  <>
                    <AccordionItem
                      buttonAutomationId="ExpensesToApproveHeading"
                      heading={t('ExpensesToApproveHeading')}
                      counter={totalExpensesForApproval}
                      dataAutomationId="NotificationCenterItemExpenses"
                      handleChange={() =>
                        toggleItem(accordionMapping.expensesToApprove)
                      }
                      expanded={indices.includes(
                        accordionMapping.expensesToApprove,
                      )}
                    >
                      {totalExpensesForApproval > 0 ? (
                        approveExpensesUrl && (
                          <Link
                            href={`${formatUrl(approveExpensesUrl)}${expenseReportQueryParams}`}
                            data-automation-id="ApproveExpensesLink"
                          >
                            {t('ExpensesToApproveLinkText')}
                          </Link>
                        )
                      ) : (
                        <Alert
                          color="success"
                          data-automation-id="NotificationCenterAlertExpensesToBeApproved"
                        >
                          <Typography>
                            {t('ExpensesToApproveDoneText')}
                          </Typography>
                        </Alert>
                      )}
                    </AccordionItem>

                    <AccordionItem
                      buttonAutomationId="MileageToApproveHeading"
                      heading={t('MileageToApproveHeading')}
                      counter={numberOfMileagePendingApproval}
                      dataAutomationId="NotificationCenterItemMileage"
                      handleChange={() =>
                        toggleItem(accordionMapping.mileageToApprove)
                      }
                      expanded={indices.includes(
                        accordionMapping.mileageToApprove,
                      )}
                    >
                      {numberOfMileagePendingApproval > 0 ? (
                        approveExpensesUrl && (
                          <Link
                            href={`${formatUrl(approveExpensesUrl)}?mileage=true`}
                            data-automation-id="ApproveMileageLink"
                          >
                            {t('MileageToApproveLinkText')}
                          </Link>
                        )
                      ) : (
                        <Alert
                          color="success"
                          data-automation-id="NotificationCenterAlertMileageToBeApproved"
                        >
                          <Typography>
                            {t('MileageToApproveDoneText')}
                          </Typography>
                        </Alert>
                      )}
                    </AccordionItem>
                  </>
                )}
              </div>
            </div>
          )}
        </Stack>
      ) : null}
    </ResponseHandler>
  );
};
